import {
  ChangeDetectionStrategy,
  Component,
  computed,
  effect,
  inject,
  input,
  output
} from '@angular/core';
import {Store} from '@ngrx/store';
import {FormBuilder, FormGroup, FormsModule, ReactiveFormsModule, Validators} from '@angular/forms';
import {PaperChatFilterType} from '../../dto/paper-chat';
import {selectFilterType} from '../../service/paper-chat.selector';
import {loadProjectHasEmbeddingOverview} from '../../service/embedding.actions';
import {selectEmbeddingState} from '../../service/embedding.selectors';
import {setChatFilterType} from '../../service/paper-chat.actions';
import {ProjectHasEmbeddingOverviewDTO} from '../../dto/embedding';

import {MatFormFieldModule} from '@angular/material/form-field';
import {MatInputModule} from '@angular/material/input';
import {MatSelectModule} from '@angular/material/select';
import {MatButtonModule} from '@angular/material/button';


interface Option {
  value: string;
  viewValue: string;
  enabled: boolean;
  count?: number;
}

@Component({
  selector: 'app-paper-chat-filter',
  imports: [
    FormsModule,
    ReactiveFormsModule,
    MatFormFieldModule,
    MatInputModule,
    MatSelectModule,
    MatButtonModule
],
  templateUrl: './paper-chat-filter.component.html',
  styleUrl: './paper-chat-filter.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class PaperChatFilterComponent {
  private readonly store: Store = inject(Store);
  private readonly fb: FormBuilder = inject(FormBuilder);

  readonly projectId = input.required<number>();
  readonly changeOnSave = input<boolean>(false);
  readonly showHint = input<boolean>(false);
  readonly invalid = output<boolean>()

  private readonly baseOptions: Option[] = [
    {value: PaperChatFilterType.ALL, viewValue: 'All', enabled: true},
    {value: PaperChatFilterType.SCREENING_ONE, viewValue: 'Passed Screening One', enabled: false},
    {value: PaperChatFilterType.SCREENING_TWO, viewValue: 'Passed Screening Two', enabled: false}
  ];
  private readonly embeddingState = this.store.selectSignal(selectEmbeddingState);
  readonly filterType = this.store.selectSignal(selectFilterType());
  readonly overview = computed(() => this.embeddingState().projectEmbeddingsOverview[this.projectId()] ?? null);
  readonly options = computed(() => {
    const overview = this.overview();
    if (!overview) {
      return this.baseOptions;
    }
    return this.baseOptions.map((option) => ({
      ...option,
      enabled: this.getEmbeddingCount(option.value, overview) > 0,
      count: this.getEmbeddingCount(option.value, overview),
    }));
  });

  form: FormGroup = this.fb.group(
    {
      paperFilterSelect: ['', Validators.required]
    }
  );

  constructor() {
    effect(() => {
      this.form.patchValue({paperFilterSelect: this.filterType()}, {emitEvent: false});
    });

    effect(() => {
      this.store.dispatch(loadProjectHasEmbeddingOverview({projectId: this.projectId()}));
    });

    this.form.valueChanges.subscribe(() => {
      this.invalid.emit(this.form.invalid);
      if (this.changeOnSave()) {
        this.save();
      }
    });
  }

  save(): void {
    this.store.dispatch(() => setChatFilterType({
      filterType: this.form.value.paperFilterSelect,
      projectId: this.projectId()
    }));
  }

  getEmbeddingCount(filterType: string, overview: ProjectHasEmbeddingOverviewDTO): number {
    if (filterType === PaperChatFilterType.ALL) {
      return overview.all;
    } else if (filterType === PaperChatFilterType.SCREENING_ONE) {
      return overview.passedScreeningOne;
    } else if (filterType === PaperChatFilterType.SCREENING_TWO) {
      return overview.passedScreeningTwo;
    }
    return 0;
  }

}
